import AppFormContainer from "@/components/AppFormContainer";
import ResultPanel from "@/components/comm-generator/ResultPanel";
import SideTitle from "@/components/SideTitle";
import SplitPane from "@/components/SplitPane";
import { filterFormPreferences } from "@/lib/utils/filterFormPreferences";
import {
  AGE_GROUPS,
  LEARNING_REQUIREMENTS,
  LESSON_TYPES,
} from "@/lib/types/comms";
import { Sparkle } from "@phosphor-icons/react";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useUserTemplates } from "@/lib/queries/user/useTemplates";
import { TemplateUploadModal } from "@/components/TemplateUploadModal";
import useInit from "@/lib/queries/user/useInit";
import { SubjectInput } from "@/components/lesson-lab/SubjectInput";
import { LessonTypesSection } from "@/components/lesson-lab/LessonTypesSection";
import { LanguageSelector } from "@/components/lesson-lab/LanguageSelector";
import { AccessibilityOptionsToggle } from "@/components/lesson-lab/AccessibilityOptionsToggle";
import { useTranslation } from "react-i18next";
import TutorialBox, { useTutorialState } from "@/components/TutorialBox";
import { GuidedTour, TourStep } from "@/components/GuidedTour";
import { DEFAULT_RESULT_LANGUAGE } from "@/lib/constants/languages";
import { resolveInitialToolLanguage } from "@/lib/utils/language";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import { useToolSubmissionSSE } from "@/lib/queries/tools/useToolSubmissionSSE";
import FloatingDocStatuses from "@/components/results/FloatingDocStatuses";
import { buildDocStatuses } from "@/components/results/buildDocStatuses";
import { useToolSubmission } from "@/lib/queries/tools/useToolSubmission";
import useSessionStorage from "@/lib/hooks/useSessionStorage";
import { useStytchSession } from "@stytch/react";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import { OptionGroup } from "@/components/comm-generator/A11yOptions";
import { useIsMobile } from "@/hooks/use-mobile";

const USE_SSE = true;

const getBadgeStyles = (category?: string, subcategory?: string) => {
  if (!category) return "text-gray-700 bg-gray-100";

  switch (category) {
    case "Resource":
      return "text-blue-800 bg-blue-100 border-blue-200";
    case "Communication":
      switch (subcategory) {
        case "Parents":
          return "text-green-800 bg-green-100 border-green-200";
        case "Students":
          return "text-purple-800 bg-purple-100 border-purple-200";
        case "School":
          return "text-orange-800 bg-orange-100 border-orange-200";
        case "Other":
          return "text-pink-800 bg-pink-100 border-pink-200";
        default:
          return "text-emerald-800 bg-emerald-100 border-emerald-200";
      }
    default:
      return "text-gray-700 bg-gray-100";
  }
};

type LessonLabForm = {
  subject: string;
  type: Array<{
    name: string;
    duration: string;
    checked: boolean;
    selectedTemplate: string;
  }>;
  requirements: { language: string };
  age: string;
  learning_requirement: string;
  showAccessibilityOptions: string;
  files: any;
  fileUrls: string[];
};

// Use shared resolver: a11y > resultLanguage > displayLanguage > default (display default here)
function resolveInitialLanguage(
  userSettings: any | undefined,
  a11yLanguage?: string | null
): string {
  return resolveInitialToolLanguage({
    a11yLanguage,
    userSettings,
    fallback: DEFAULT_RESULT_LANGUAGE,
  });
}

function LessonLab() {
  const { t } = useTranslation(["lessonLab", "common"]);
  const isMobile = useIsMobile();
  const { session, fromCache } = useStytchSession();
  const { useGetUserSettings } = useUserSettings();
  const { data: userSettings } = useGetUserSettings({
    enabled: !!session && !fromCache,
  });
  const { language: a11yLanguage } = useA11ySettings();
  const initialLanguage = useMemo(
    () => resolveInitialLanguage(userSettings, a11yLanguage),
    [userSettings, a11yLanguage]
  );

  const [showAllTypes, setShowAllTypes] = useState(false);

  const { data: initData } = useInit();
  const isTemplateFeatureEnabled = useMemo(
    () =>
      initData?.features?.some((f) => f.name === "Templates" && f.active) ??
      false,
    [initData]
  );
  const { data: templatesData } = useUserTemplates("Resource", {
    enabled: isTemplateFeatureEnabled,
  });
  const [templateUploadModalOpen, setTemplateUploadModalOpen] = useState(false);
  const resolver = (values: LessonLabForm) => {
    const errors: any = {};
    const atLeastOneChecked = values.type.some((i) => i.checked);
    if (!atLeastOneChecked) {
      errors.type = { message: t("materials.error"), type: "manual" };
    }
    return { values, errors } as any;
  };

  const form = useForm<LessonLabForm>({
    defaultValues: {
      subject: "",
      type: LESSON_TYPES.map((lt) => {
        const isLessonPlan = lt.value === "Lesson Plan";
        const isWorksheet = lt.value === "Worksheet";
        const defaultDuration = isLessonPlan
          ? "45 minutes"
          : isWorksheet
            ? "15 minutes"
            : lt.durations[0];
        return {
          name: lt.value,
          duration: defaultDuration,
          checked: isLessonPlan || isWorksheet,
          selectedTemplate: "__no_template__",
        };
      }),
      requirements: { language: initialLanguage },
      age: AGE_GROUPS.UNSET,
      learning_requirement: LEARNING_REQUIREMENTS.UNSET,
      showAccessibilityOptions: "no",
      files: null,
      fileUrls: [],
    },
    resolver,
  });
  const sse = USE_SSE ? useToolSubmissionSSE("lessonlab") : (null as any);
  const mutation = !USE_SSE ? useToolSubmission("lessonlab") : (null as any);
  const isPending = USE_SSE ? sse.isStreaming : Boolean(mutation?.isPending);
  const isSuccess = USE_SSE
    ? Boolean(sse.final || sse.latestOutput)
    : Boolean(mutation?.data);
  const data = USE_SSE ? sse.final : mutation?.data;
  const [storedIntermediate, setStoredIntermediate] = useState<string | null>(
    null
  );
  const [lastSubject, setLastSubject] = useState<string | null>(null);
  const [savePreferencesEnabled, setSavePreferencesEnabled] = useState(false);
  const [getPreferences, setPreferences] = useSessionStorage(
    "lesson-lab-preferences",
    {}
  );
  // Save preferences
  useEffect(() => {
    const sub = form.watch((value) => {
      if (!value) return;
      if (form.formState.isDirty && savePreferencesEnabled) {
        const filtered = filterFormPreferences(value, ["teacher"]);
        setPreferences(filtered);
      }
    });
    return () => sub.unsubscribe();
  }, [form, savePreferencesEnabled, setPreferences]);
  // Load preferences
  useEffect(() => {
    if (getPreferences && Object.keys(getPreferences).length > 0) {
      form.reset({ ...form.getValues(), ...getPreferences });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Sync language if settings/a11y change and field untouched
  useEffect(() => {
    const desired = resolveInitialLanguage(userSettings, a11yLanguage);
    const current = form.getValues("requirements.language");
    const dirty = (form.formState.dirtyFields as any)?.requirements?.language;
    if (!dirty && current !== desired) {
      form.setValue("requirements.language", desired, { shouldDirty: false });
    }
  }, [userSettings, a11yLanguage, form, initialLanguage]);
  const onSubmit = form.handleSubmit((values) => {
    const subjectChanged =
      lastSubject !== null && lastSubject !== values.subject;
    const intermediateValue = subjectChanged ? null : storedIntermediate;
    const selected = values.type.filter((f) => f.checked);
    const payload = {
      subject: values.subject,
      type: selected.map((typeItem) => ({
        name: typeItem.name,
        recipient: "student",
        duration: parseInt(typeItem.duration),
        durationUnit: typeItem.duration.split(" ")[1],
        templateId:
          typeItem.selectedTemplate &&
          typeof typeItem.selectedTemplate === "string" &&
          typeItem.selectedTemplate !== "__no_template__"
            ? typeItem.selectedTemplate
            : undefined,
      })),
      requirements: {
        age:
          values.age === AGE_GROUPS.UNSET
            ? null
            : t(values.age, { lng: "en", ns: "common" }),
        language: t(values.requirements.language, { lng: "en", ns: "common" }),
        learning_requirement:
          values.learning_requirement === LEARNING_REQUIREMENTS.UNSET
            ? null
            : t(values.learning_requirement, { lng: "en", ns: "common" }),
      },
      regenerate: { intermediate: intermediateValue, feedback: "" },
      attachments: values.fileUrls,
    } as const;
    if (USE_SSE) {
      sse.start(payload as any);
    } else {
      (mutation as any).mutate(payload as any);
    }
    setLastSubject(values.subject);
  });
  // Update storedIntermediate when response returns
  useEffect(() => {
    if (isSuccess && data?.intermediate) {
      setStoredIntermediate(data.intermediate);
    }
  }, [isSuccess, data?.intermediate]);
  // Subscribe only to 'type' errors to avoid parent rerenders on unrelated field changes (like typing subject)
  const { errors } = useFormState({ control: form.control, name: ["type"] });
  const typeError = (errors as any)?.type?.message;
  const [, setForceRender] = useState(0);
  const forceUpdate = useCallback(() => setForceRender((p) => p + 1), []);

  const [tourOpen, setTourOpen] = useState(false);
  const tutorialState = useTutorialState("lessonlab-tutorial");
  const tourSteps: TourStep[] = useMemo(
    () => [
      {
        selector: "#side-title",
        title: t("tutorial.1.title"),
        content: t("tutorial.1.body", { ns: "lessonLab" }),
      },
      {
        selector: "#subject-form-item",
        title: t("tutorial.2.title"),
        content: t("tutorial.2.body", { ns: "lessonLab" }),
      },
      {
        selector: "[data-testid=lesson-types]",
        title: t("tutorial.3.title"),
        content: t("tutorial.3.body", { ns: "lessonLab" }),
      },
      {
        selector: "#tour-language-container",
        title: t("tutorial.4.title"),
        content: t("tutorial.4.body", { ns: "lessonLab" }),
      },
      {
        selector: "#tour-generate",
        title: t("tutorial.5.title"),
        content: t("tutorial.5.body", { ns: "lessonLab" }),
      },
    ],
    [t]
  );

  const getTemplatesForType = useMemo(() => {
    return (typeName: string) => {
      if (!templatesData?.templates) return [];
      return templatesData.templates.filter((template) =>
        template.tags.some((tag) => {
          if (tag.category === "resource" && tag.general && !tag.subcategory)
            return true;
          if (tag.category === "resource" && tag.tagText === typeName)
            return true;
          return false;
        })
      );
    };
  }, [templatesData?.templates]);

  // Stable callback so memoized children don't rerender on subject typing
  const handleTypeCheck = useCallback(
    (typeIndex: number, checked: boolean) => {
      if (!checked || !isTemplateFeatureEnabled) return;
      const currentTypes = form.getValues("type");
      const type = currentTypes[typeIndex];
      if (type && type.selectedTemplate === "__no_template__") {
        const available = getTemplatesForType(type.name);
        if (available.length > 0)
          form.setValue(`type.${typeIndex}.selectedTemplate`, available[0].id);
      }
    },
    [form, getTemplatesForType, isTemplateFeatureEnabled]
  );

  return (
    <>
      <SplitPane
        showResultPanel={
          USE_SSE
            ? Boolean(sse.final || sse.latestOutput)
            : Boolean(mutation?.data)
        }
        key={isSuccess ? "lesson-lab-result" : "lesson-lab-form"}
      >
        <AppFormContainer
          form={form}
          onSubmit={onSubmit}
          isSuccess={isSuccess}
          isPending={isPending}
          savePreferences={{
            enabled: savePreferencesEnabled,
            onToggle: setSavePreferencesEnabled,
          }}
        >
          <SideTitle
            title={t("header.title")}
            intro={t("header.intro")}
            icon={Sparkle}
            showHelpIndicator={tutorialState.dismissed}
            onHelpClick={tutorialState.reopen}
          />

          <TutorialBox
            storageKey="lessonlab-tutorial"
            title={t("common:quickGuide", { defaultValue: "Quick guide" })}
            message={
              t("tutorial.blurb", {
                ns: "lessonLab",
                defaultValue: "Take a quick tour of Lesson Lab.",
              }) || "Take a quick tour of Lesson Lab."
            }
            onStartTour={() => setTourOpen(true)}
            tutorialState={tutorialState}
          />
          <GuidedTour
            open={tourOpen}
            steps={tourSteps}
            storageKey="lessonlab-tutorial"
            onClose={() => setTourOpen(false)}
          />

          <SubjectInput
            tool="lessonLab"
            control={form.control}
            onForceUpdate={forceUpdate}
          />

          <LessonTypesSection
            control={form.control}
            setValue={form.setValue}
            lessonTypes={LESSON_TYPES}
            showAllTypes={showAllTypes}
            onToggleShowAll={() => setShowAllTypes(!showAllTypes)}
            typeError={typeError}
            getTemplatesForType={getTemplatesForType}
            isTemplateFeatureEnabled={isTemplateFeatureEnabled}
            onTemplateUpload={() => setTemplateUploadModalOpen(true)}
            getBadgeStyles={getBadgeStyles}
            onTypeCheck={handleTypeCheck}
          />

          <LanguageSelector name="requirements.language" />

          <OptionGroup
            name="age"
            options={AGE_GROUPS}
            label="Age Group"
            skipUnset={false}
          />

          <AccessibilityOptionsToggle
            control={form.control}
            watch={form.watch}
            t={t}
          />
        </AppFormContainer>
        <ResultPanel
          result={
            USE_SSE
              ? sse.final
                ? sse.final.results
                : sse.latestOutput
                  ? [sse.latestOutput]
                  : undefined
              : mutation?.data?.results
          }
          appResult="lesson-lab"
          isLoading={isPending}
          toolResponse={USE_SSE ? data : mutation?.data}
          sseEvents={USE_SSE ? sse.events : undefined}
          sseError={USE_SSE ? sse.error : (mutation?.error as any)}
          loadingStatuses={
            USE_SSE
              ? buildDocStatuses({
                  expectedDocs: (form.getValues("type") || [])
                    .filter((t: any) => t.checked)
                    .map((tp: any) => ({
                      type: tp.name,
                      language:
                        (form.getValues("requirements.language") as string) ||
                        initialLanguage,
                    })),
                  sseEvents: sse.events,
                  results:
                    (data?.results as any) ||
                    (sse.latestOutput ? [sse.latestOutput] : []),
                  isStreaming: sse.isStreaming,
                  isLoading: isPending,
                })
              : undefined
          }
          loadingStage={USE_SSE ? sse.stage : undefined}
          loadingProgress={USE_SSE ? sse.progress : undefined}
          onCancel={USE_SSE ? sse.abort : undefined}
        />
      </SplitPane>

      {USE_SSE &&
        (() => {
          const docStatuses = buildDocStatuses({
            expectedDocs: (form.getValues("type") || [])
              .filter((t: any) => t.checked)
              .map((tp: any) => ({
                type: tp.name,
                language:
                  (form.getValues("requirements.language") as string) ||
                  initialLanguage,
              })),
            sseEvents: sse.events,
            results:
              (data?.results as any) ||
              (sse.latestOutput ? [sse.latestOutput] : []),
            isStreaming: sse.isStreaming,
            isLoading: isPending,
          });
          const hasReady = docStatuses.some((s) => s.status === "ready");
          const showFloating =
            Boolean(sse.isStreaming) &&
            (isMobile || hasReady) &&
            docStatuses.length > 0 &&
            sse.stage !== "done" &&
            !sse.error;
          return (
            <FloatingDocStatuses
              visible={showFloating}
              statuses={docStatuses}
              stage={sse.stage}
              progress={sse.progress}
              onClose={() => {}}
              onCancel={sse.abort}
            />
          );
        })()}

      {isTemplateFeatureEnabled && (
        <TemplateUploadModal
          open={templateUploadModalOpen}
          onOpenChange={setTemplateUploadModalOpen}
          onSuccess={() => {
            setTemplateUploadModalOpen(false);
          }}
        />
      )}
    </>
  );
}

export default LessonLab;
